/*
 * © 2026 Ecsper Ltd. All rights reserved.
 * SPDX-License-Identifier: LicenseRef-Ecsper-Proprietary
 * Unauthorised use, copying, or distribution is strictly prohibited.
 */

import { Button, Modal } from '@ecsper/ui-primitives';
import type { ReportVersionSummary } from '@ecsper/vault-client';
import type { ReactElement } from 'react';
import { useEffect, useMemo, useState } from 'react';

import { formatRelativeTime } from './format-relative-time.js';

export interface VersionHistoryProps {
  /** Versions list (from VaultClient.listVersions()). */
  versions: ReportVersionSummary[];
  /** Loading state for the list itself. */
  isLoading?: boolean;
  /** Error string if list fetch failed. */
  loadError?: string;
  /** Called when user clicks Refresh. */
  onRefresh?: () => void;
  /** Version number to highlight after a fresh save. */
  highlightVersionNumber?: number;
  /** Called when user selects a version to restore (Stage 6). */
  onRestore?: (versionId: string) => void;
}

const VISIBLE_VERSION_LIMIT = 5;
const COLLAPSE_THRESHOLD = 3;

function truncateActorId(actorId: string): string {
  if (actorId.length <= 10) {
    return actorId;
  }
  return `${actorId.slice(0, 8)}…`;
}

function saveSourceBadgeLabel(saveSource: string): string {
  const normalised = saveSource.toLowerCase();
  if (normalised.includes('word') || normalised.includes('addin')) {
    return 'Word';
  }
  if (normalised.includes('api')) {
    return 'API';
  }
  return 'System';
}

function formatFullTimestamp(iso: string): string {
  const parsed = Date.parse(iso);
  if (Number.isNaN(parsed)) {
    return iso;
  }
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'full',
    timeStyle: 'medium',
  }).format(new Date(parsed));
}

function saveSourceDetailLabel(saveSource: string): string {
  const normalised = saveSource.toLowerCase();
  if (normalised.includes('word') || normalised.includes('addin')) {
    return 'Word add-in';
  }
  if (normalised.includes('api')) {
    return 'API';
  }
  if (
    normalised.includes('editor') ||
    normalised.includes('manual') ||
    normalised.includes('web')
  ) {
    return 'Editor';
  }
  const label = saveSource.trim();
  return label !== '' ? label : 'Unknown source';
}

function ChevronIcon({ expanded }: { readonly expanded: boolean }): ReactElement {
  return (
    <svg
      className={`ecs-versions__chevron${expanded ? ' ecs-versions__chevron--open' : ''}`}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 6l6 6-6 6" />
    </svg>
  );
}

function MajorIndicator(): ReactElement {
  return (
    <svg
      className="ecs-versions__major-icon"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6L12 2z" />
    </svg>
  );
}

export function VersionHistory({
  versions,
  isLoading = false,
  loadError,
  onRefresh,
  highlightVersionNumber,
  onRestore: _onRestore,
}: VersionHistoryProps): ReactElement {
  const [detailId, setDetailId] = useState<string | null>(null);
  const [isSectionOpen, setIsSectionOpen] = useState(() => versions.length <= COLLAPSE_THRESHOLD);
  const [showAllVersions, setShowAllVersions] = useState(false);

  useEffect(() => {
    setIsSectionOpen(versions.length <= COLLAPSE_THRESHOLD);
    setShowAllVersions(false);
  }, [versions.length]);

  const detailVersion = useMemo(() => {
    if (detailId === null) {
      return undefined;
    }
    return versions.find((v) => v.id === detailId);
  }, [detailId, versions]);

  const visibleVersions = showAllVersions ? versions : versions.slice(0, VISIBLE_VERSION_LIMIT);
  const hasHiddenVersions = versions.length > VISIBLE_VERSION_LIMIT;

  const listContent = (() => {
    if (isLoading) {
      return (
        <>
          <p className="ecs-versions__loading ecs-sr-only">Loading versions…</p>
          <div className="ecs-versions__skeleton" aria-hidden="true">
            <div className="ecs-skeleton ecs-skeleton--version-row" />
            <div className="ecs-skeleton ecs-skeleton--version-row" />
            <div className="ecs-skeleton ecs-skeleton--version-row" />
          </div>
        </>
      );
    }
    if (loadError !== undefined && loadError !== '') {
      return (
        <div className="ecs-versions__error-block">
          <p className="ecs-versions__error" role="alert">
            {loadError}
          </p>
          {onRefresh !== undefined ? (
            <div className="ecs-versions__error-actions">
              <button
                type="button"
                className="ecs-btn ecs-btn--secondary"
                onClick={() => {
                  onRefresh();
                }}
              >
                Retry
              </button>
            </div>
          ) : null}
        </div>
      );
    }
    if (versions.length === 0) {
      return <p className="ecs-versions__empty">No versions saved yet.</p>;
    }
    return (
      <>
        <ul className="ecs-versions__list">
          {visibleVersions.map((v) => {
            const rel = formatRelativeTime(v.createdAt);
            const badge = saveSourceBadgeLabel(v.saveSource);
            const itemClasses = [
              'ecs-versions__item',
              v.isMajor ? 'ecs-versions__item--major' : '',
              highlightVersionNumber === v.versionNumber ? 'ecs-versions__item--highlight' : '',
            ]
              .filter(Boolean)
              .join(' ');
            return (
              <li key={v.id}>
                <button
                  type="button"
                  className={itemClasses}
                  onClick={() => {
                    setDetailId(v.id);
                  }}
                >
                  <span className="ecs-versions__item-main">
                    {v.isMajor ? <MajorIndicator /> : null}
                    <span className="ecs-versions__number">v{v.versionNumber}</span>
                    <span className="ecs-badge ecs-badge--compact ecs-versions__source">
                      {badge}
                    </span>
                  </span>
                  <time className="ecs-versions__time" dateTime={v.createdAt}>
                    {rel}
                  </time>
                </button>
              </li>
            );
          })}
        </ul>
        {hasHiddenVersions ? (
          <button
            type="button"
            className="ecs-link ecs-versions__expand"
            onClick={() => {
              setShowAllVersions((prev) => !prev);
            }}
          >
            {showAllVersions ? 'Show less' : `Show all (${String(versions.length)})`}
          </button>
        ) : null}
      </>
    );
  })();

  const hashShort =
    detailVersion !== undefined && detailVersion.contentHash.length >= 8
      ? detailVersion.contentHash.slice(0, 8)
      : (detailVersion?.contentHash ?? '');

  return (
    <section className="ecs-panel ecs-versions">
      <div className="ecs-versions__header">
        <button
          type="button"
          className="ecs-versions__toggle"
          aria-expanded={isSectionOpen}
          onClick={() => {
            setIsSectionOpen((prev) => !prev);
          }}
        >
          <ChevronIcon expanded={isSectionOpen} />
          <span className="ecs-versions__title">Version history</span>
        </button>
        {onRefresh !== undefined && loadError === undefined ? (
          <button
            type="button"
            className="ecs-link ecs-versions__refresh"
            onClick={() => {
              onRefresh();
            }}
          >
            Refresh
          </button>
        ) : null}
      </div>
      <div
        className={`ecs-versions__body${isSectionOpen ? ' ecs-versions__body--open' : ''}`}
        hidden={!isSectionOpen}
      >
        {listContent}
      </div>
      <Modal
        open={detailVersion !== undefined}
        onClose={() => {
          setDetailId(null);
        }}
        title={detailVersion !== undefined ? `Version v${String(detailVersion.versionNumber)}` : ''}
      >
        {detailVersion !== undefined ? (
          <div className="ecs-version-history-detail__grid">
            <div className="ecs-version-history-detail__field">
              <span className="ecs-version-history-detail__label">Saved at</span>
              <p className="ecs-version-history-detail__value">
                {formatFullTimestamp(detailVersion.createdAt)}
              </p>
            </div>
            <div className="ecs-version-history-detail__field">
              <span className="ecs-version-history-detail__label">Content hash</span>
              <p className="ecs-version-history-detail__value" title={detailVersion.contentHash}>
                {hashShort}
              </p>
            </div>
            <div className="ecs-version-history-detail__field">
              <span className="ecs-version-history-detail__label">Save source</span>
              <p className="ecs-version-history-detail__value">
                {saveSourceDetailLabel(detailVersion.saveSource)}
              </p>
            </div>
            <div className="ecs-version-history-detail__field">
              <span className="ecs-version-history-detail__label">Author</span>
              <p className="ecs-version-history-detail__value">
                {truncateActorId(detailVersion.createdByActorId)}
              </p>
            </div>
            <div className="ecs-version-history-detail__actions">
              <Button
                type="button"
                variant="secondary"
                size="md"
                disabled
                title="Available in Stage 6"
              >
                Restore this version
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>
    </section>
  );
}
